import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, EstimateButton } from "@/components/Shared";
import { SERVICES } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

export default function Services() {
  useSEO(
    "Renovation Services in the GTA | Kitchens, Bathrooms, Basements & More",
    "Kitchen renovations, bathroom renovations, basement finishing, flooring, interior renovations and painting, and complete home renovations across the Greater Toronto Area. Free estimates: (519) 760-0444."
  );

  return (
    <>
      <section className="py-16 sm:py-24 border-b border-obsidian/10" data-testid="services-header">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-stonemuted">Services · Greater Toronto Area</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-obsidian max-w-2xl">
              Renovation services, done properly.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-stone-600 font-light leading-relaxed">
              From a single bathroom to a whole-home transformation — scoped in writing, built by our own crew, and finished to a professional standard.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 border-b border-obsidian/10" data-testid="services-grid">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08} className="border border-obsidian/15 bg-white flex flex-col">
              <div className="overflow-hidden aspect-[4/3]">
                <img src={s.image} alt={`${s.title} — renovation contractor GTA`} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h2 className="font-display text-xl font-medium text-obsidian">{s.title}</h2>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed flex-1">{s.desc}</p>
                <Link
                  to="/estimate"
                  state={{ projectType: s.title }}
                  data-testid={`service-quote-${s.id}`}
                  className="group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teak hover:text-teakdark transition-colors"
                >
                  Book a free estimate <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-parchment/60" data-testid="service-area-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-stonemuted">Service Area</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-medium tracking-tight text-obsidian">
              Serving the GTA & Kitchener-Waterloo
            </h2>
            <p className="mt-4 text-sm text-stone-600 max-w-xl mx-auto leading-relaxed">
              Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Oakville, Burlington, Kitchener, Waterloo, Cambridge and Guelph.
            </p>
            <div className="mt-8"><EstimateButton testId="services-estimate-button" /></div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
